import { Locale } from 'date-fns';
import {
  bg,
  cs,
  de,
  el,
  enAU,
  enCA,
  enGB,
  enIN,
  enNZ,
  enUS,
  enZA,
  es,
  fi,
  fr,
  frCA,
  frCH,
  hu,
  it,
  ja,
  ko,
  nb,
  nl,
  nlBE,
  pl,
  pt,
  ro,
  ru,
  sv,
  th,
  tr,
  vi,
  zhCN,
  zhTW
} from 'date-fns/locale';

import { datePickerInputFormats, DatePickerInputFormat } from '@components/library/date-picker';
import { IsoLanguagesRegions } from '@components/library/utils';

interface InputFormatLocale {
  dateFnsLocale: Locale;
  datePickerInputFormat: DatePickerInputFormat;
}

/**
 * Get a date-fns Locale and DatePickerInputFormat from a given ISO language code.
 * NOTE: Boulder only supports two letter codes right now, like "en".
 * This function also handles region, like "en-US", which is needed for proper date input formatting.
 * @param locale string
 * @returns InputFormatLocale
 */
export const getInputFormatLocale = (locale: string): InputFormatLocale => {
  switch (locale) {
    case IsoLanguagesRegions.BG:
      return { dateFnsLocale: bg, datePickerInputFormat: datePickerInputFormats[0] };
    case IsoLanguagesRegions.CS:
      return { dateFnsLocale: cs, datePickerInputFormat: datePickerInputFormats[0] };
    case IsoLanguagesRegions.DE:
      return { dateFnsLocale: de, datePickerInputFormat: datePickerInputFormats[0] };
    case IsoLanguagesRegions.EL:
      return { dateFnsLocale: el, datePickerInputFormat: datePickerInputFormats[0] };
    case IsoLanguagesRegions.EN_AU:
      return { dateFnsLocale: enAU, datePickerInputFormat: datePickerInputFormats[0] };
    case IsoLanguagesRegions.EN_CA:
      return { dateFnsLocale: enCA, datePickerInputFormat: datePickerInputFormats[2] };
    case IsoLanguagesRegions.EN_GB:
      return { dateFnsLocale: enGB, datePickerInputFormat: datePickerInputFormats[0] };
    case IsoLanguagesRegions.EN_IN:
      return { dateFnsLocale: enIN, datePickerInputFormat: datePickerInputFormats[0] };
    case IsoLanguagesRegions.EN_NZ:
      return { dateFnsLocale: enNZ, datePickerInputFormat: datePickerInputFormats[0] };
    // NOTE: EN_US is below as the default
    case IsoLanguagesRegions.EN_ZA:
      return { dateFnsLocale: enZA, datePickerInputFormat: datePickerInputFormats[2] };
    case IsoLanguagesRegions.ES:
      return { dateFnsLocale: es, datePickerInputFormat: datePickerInputFormats[0] };
    case IsoLanguagesRegions.FI:
      return { dateFnsLocale: fi, datePickerInputFormat: datePickerInputFormats[0] };
    case IsoLanguagesRegions.FR:
      return { dateFnsLocale: fr, datePickerInputFormat: datePickerInputFormats[0] };
    case IsoLanguagesRegions.FR_CA:
      return { dateFnsLocale: frCA, datePickerInputFormat: datePickerInputFormats[2] };
    case IsoLanguagesRegions.FR_CH:
      return { dateFnsLocale: frCH, datePickerInputFormat: datePickerInputFormats[0] };
    case IsoLanguagesRegions.HU:
      return { dateFnsLocale: hu, datePickerInputFormat: datePickerInputFormats[2] };
    case IsoLanguagesRegions.IT:
      return { dateFnsLocale: it, datePickerInputFormat: datePickerInputFormats[0] };
    case IsoLanguagesRegions.JA:
      return { dateFnsLocale: ja, datePickerInputFormat: datePickerInputFormats[2] };
    case IsoLanguagesRegions.KO:
      return { dateFnsLocale: ko, datePickerInputFormat: datePickerInputFormats[2] };
    case IsoLanguagesRegions.NB:
      return { dateFnsLocale: nb, datePickerInputFormat: datePickerInputFormats[0] };
    case IsoLanguagesRegions.NL:
      return { dateFnsLocale: nl, datePickerInputFormat: datePickerInputFormats[0] };
    case IsoLanguagesRegions.NL_BE:
      return { dateFnsLocale: nlBE, datePickerInputFormat: datePickerInputFormats[0] };
    case IsoLanguagesRegions.PL:
      return { dateFnsLocale: pl, datePickerInputFormat: datePickerInputFormats[2] };
    case IsoLanguagesRegions.PT:
      return { dateFnsLocale: pt, datePickerInputFormat: datePickerInputFormats[0] };
    case IsoLanguagesRegions.RO:
      return { dateFnsLocale: ro, datePickerInputFormat: datePickerInputFormats[0] };
    case IsoLanguagesRegions.RU:
      return { dateFnsLocale: ru, datePickerInputFormat: datePickerInputFormats[0] };
    case IsoLanguagesRegions.SV:
      return { dateFnsLocale: sv, datePickerInputFormat: datePickerInputFormats[2] };
    case IsoLanguagesRegions.TH:
      return { dateFnsLocale: th, datePickerInputFormat: datePickerInputFormats[0] };
    case IsoLanguagesRegions.TR:
      return { dateFnsLocale: tr, datePickerInputFormat: datePickerInputFormats[0] };
    case IsoLanguagesRegions.VI:
      return { dateFnsLocale: vi, datePickerInputFormat: datePickerInputFormats[2] };
    case IsoLanguagesRegions.ZH:
      return { dateFnsLocale: zhCN, datePickerInputFormat: datePickerInputFormats[2] };
    case IsoLanguagesRegions.ZH_CN:
      return { dateFnsLocale: zhCN, datePickerInputFormat: datePickerInputFormats[2] };
    case IsoLanguagesRegions.ZH_TW:
      return { dateFnsLocale: zhTW, datePickerInputFormat: datePickerInputFormats[2] };
    default: // en (same as en-US)
      return {
        dateFnsLocale: enUS,
        datePickerInputFormat: datePickerInputFormats[1]
      };
  }
};
